import { useMemo, useState } from "react";
import {
  CategoryChips,
  type CategoryFilter,
} from "../../../components/dashboard/resources/CategoryChips";
import { ResourceCard } from "../../../components/dashboard/resources/ResourceCard";
import { useResources } from "../../../hooks/useResources";
import styles from "./Resources.module.css";

const SKELETON_COUNT = 4;

export const ResourcesSection = (): JSX.Element => {
  const { status, resources, error } = useResources();
  const [filter, setFilter] = useState<CategoryFilter>("all");

  const counts = useMemo<Record<CategoryFilter, number>>(() => {
    const acc: Record<CategoryFilter, number> = {
      all: resources.length,
      tutorial: 0,
      concept: 0,
      troubleshooting: 0,
      update: 0,
      inspiration: 0,
    };
    for (const r of resources) {
      acc[r.category] += 1;
    }
    return acc;
  }, [resources]);

  const visible = useMemo(() => {
    return filter === "all" ? resources : resources.filter((r) => r.category === filter);
  }, [filter, resources]);

  return (
    <section className={styles.section} aria-labelledby="dashboard-resources-title">
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.eyebrow}>// LEARN</span>
          <h2 className={styles.title} id="dashboard-resources-title">
            Get more from your agent.
          </h2>
        </div>
        {status === "ready" && resources.length > 0 && (
          <div className={styles.headerRight}>
            <CategoryChips active={filter} counts={counts} onChange={setFilter} />
          </div>
        )}
      </div>

      <div className={styles.grid}>
        {status === "loading" &&
          Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <div key={`skel-${i}`} className={styles.skeleton} aria-hidden />
          ))}

        {status === "error" && (
          <div className={styles.empty}>
            Couldn&rsquo;t load resources{error ? ` — ${error}` : ""}.
          </div>
        )}

        {status === "ready" && resources.length === 0 && (
          <div className={styles.empty}>
            No resources yet — your team can add them under Admin → Resources.
          </div>
        )}

        {status === "ready" && resources.length > 0 && visible.length === 0 && (
          <div className={styles.empty}>
            No resources in this category yet — check back soon.
          </div>
        )}

        {status === "ready" &&
          visible.map((resource) => (
            <ResourceCard key={resource.id} resource={resource} />
          ))}
      </div>
    </section>
  );
};
